// CPU pressure monitor (Compute Pressure API).
//
// The browser exposes no CPU-% API; the Compute Pressure API
// (Chrome/Edge 125+, secure context) is the closest — it reports a coarse
// state ('nominal' | 'fair' | 'serious' | 'critical') derived from CPU
// utilisation/thermals. We use it to decide whether it's safe to (re)run
// DeepFilterNet: DFN's ONNX inference is CPU-heavy, so we don't promote
// back to DFN while pressure is serious/critical (e.g. camera encode is
// pinning the cores), and we treat a relaxed CPU as the green light to
// recover.
//
// Absent API (Firefox/Safari/older Chromium) → state stays 'unknown' and
// callers fall back to their time/camera heuristics. We never BLOCK
// behaviour on a signal we don't have ('unknown' counts as relaxed, not
// busy).

export type CpuPressureState = 'nominal' | 'fair' | 'serious' | 'critical' | 'unknown'

let current: CpuPressureState = 'unknown'
let started = false
// Typed loosely — PressureObserver isn't in the TS DOM lib yet.
let observer: { disconnect: () => void } | null = null

/**
 * Begin observing CPU pressure. Idempotent and best-effort: safe no-op
 * when the API is missing or `observe()` throws (NotSupported/Security).
 */
export function startCpuPressureMonitor(): void {
  if (started) return
  started = true
  try {
    const g = globalThis as unknown as {
      PressureObserver?: {
        new (cb: (records: Array<{ source: string; state: CpuPressureState }>) => void): {
          observe: (source: string, opts?: { sampleInterval?: number }) => Promise<void> | void
          disconnect: () => void
        }
        knownSources?: string[]
      }
    }
    const PO = g.PressureObserver
    if (!PO) return // API absent → stays 'unknown'
    if (Array.isArray(PO.knownSources) && !PO.knownSources.includes('cpu')) return
    const obs = new PO((records) => {
      const last = records[records.length - 1]
      if (last && typeof last.state === 'string') current = last.state
    })
    // 2 s sampling is ample for our coarse degrade/recover decisions.
    void obs.observe('cpu', { sampleInterval: 2000 })
    observer = obs
    console.info('[cpuPressure] Compute Pressure API active (cpu source)')
  } catch {
    // observe() can reject/throw — leave state 'unknown'.
    observer = null
  }
}

export function stopCpuPressureMonitor(): void {
  try {
    observer?.disconnect()
  } catch {
    /* noop */
  }
  observer = null
  started = false
  current = 'unknown'
}

export function getCpuPressure(): CpuPressureState {
  return current
}

/**
 * True only when we POSITIVELY know the CPU is under heavy load.
 * 'unknown' is NOT busy — never block on a missing signal.
 */
export function isCpuBusy(): boolean {
  return current === 'serious' || current === 'critical'
}

/**
 * True when the CPU is relaxed enough to (re)run DFN. 'unknown' counts as
 * relaxed so machines without the API still recover DFN via the
 * time/camera triggers.
 */
export function isCpuRelaxed(): boolean {
  return current === 'nominal' || current === 'fair' || current === 'unknown'
}
